import React, { useMemo, useState } from "react";
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  ControlButton,
  ReactFlowProvider,
  type NodeTypes,
  type EdgeTypes,
} from "reactflow";
import "reactflow/dist/style.css";
import { Box } from "@mui/material";
import { nodeTypes, edgeTypes } from "../../../../util/nodeTypes";
import { PictureInPicture } from "@phosphor-icons/react";

interface FlowPreviewProps {
  nodes: Node[] | null;
  edges: Edge[] | null;
  width?: number | string;
  height?: number | string;
}

const FlowPreviewInner: React.FC<FlowPreviewProps> = ({
  nodes = [],
  edges = [],
  width = "100%",
  height = "100%",
}) => {
  const [minimapVisible, setMinimapVisible] = useState(false);

  const flowNodes = useMemo(() => {
    if (!nodes || !Array.isArray(nodes)) return [];
    return nodes.map((node) => ({
      ...node,
      position: node.position || { x: 0, y: 0 },
      draggable: false,
      selectable: false,
    }));
  }, [nodes]);

  const flowEdges = useMemo(() => {
    if (!edges || !Array.isArray(edges)) return [];
    return edges;
  }, [edges]);

  const onMinimapToggle = () => setMinimapVisible((prevstate) => !prevstate);

  return (
    <Box
      sx={{
        width,
        height,
        position: "relative",
        "& .react-flow": {
          background: "transparent",
        },
        "& .react-flow__node": {
          cursor: "default",
        },
        "& .react-flow__handle": {
          cursor: "default",
        },
      }}
    >
      <ReactFlow
        nodes={flowNodes}
        edges={flowEdges}
        nodeTypes={nodeTypes as unknown as NodeTypes}
        edgeTypes={edgeTypes as unknown as EdgeTypes}
        fitView
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={true}
        panOnDrag={true}
        panOnScroll={false}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        preventScrolling={true}
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls showInteractive={false} showZoom={false}>
          <ControlButton onClick={onMinimapToggle}>
            <PictureInPicture weight="bold" size={24} />
          </ControlButton>
        </Controls>
        {minimapVisible && (
          <MiniMap
            nodeColor={(node) => {
              return "#3650DE";
            }}
            maskColor="rgba(0, 0, 0, 0.1)"
          />
        )}
      </ReactFlow>
    </Box>
  );
};

export const FlowPreview: React.FC<FlowPreviewProps> = (props) => {
  return (
    <ReactFlowProvider>
      <FlowPreviewInner {...props} />
    </ReactFlowProvider>
  );
};
